import { useEffect, useSyncExternalStore } from 'react';
import type { PriceBase, ProjectDocument } from '@indusketch/project-model';
import type { PriceCatalog } from '@indusketch/shared';
import { pricesApi } from '../../lib/api/endpoints.js';

/** The admins' price catalogs, loaded once per page and shared by estimate and admin area. */
let catalogs: readonly PriceCatalog[] | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function load(): Promise<void> {
  loading ??= pricesApi
    .list()
    .then((r) => {
      catalogs = r.catalogs;
    })
    // Without catalogs (offline, signed out) the estimate keeps the built-in guide prices.
    .catch(() => {
      catalogs = [];
    })
    .finally(() => {
      loading = null;
      notify();
    });
  return loading;
}

/** Reloads the catalogs after an admin changed them. */
export function reloadPriceCatalogs(): Promise<void> {
  return load();
}

export function usePriceCatalogs(): readonly PriceCatalog[] | null {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => catalogs,
  );
  useEffect(() => {
    if (catalogs === null) void load();
  }, []);
  return value;
}

/** Catalog a project is priced from: its own choice, else the default one. */
export function projectCatalog(
  list: readonly PriceCatalog[] | null,
  doc: Pick<ProjectDocument, 'settings'>,
): PriceCatalog | undefined {
  if (!list) return undefined;
  const chosen = doc.settings.costs?.catalogId;
  return list.find((c) => c.id === chosen) ?? list.find((c) => c.isDefault) ?? list[0];
}

export const priceBaseOf = (catalog: PriceCatalog | undefined): PriceBase | undefined =>
  catalog
    ? { name: catalog.name, prices: catalog.prices, surcharges: catalog.surcharges }
    : undefined;
